import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { SkeletonStats } from '@/components/ui/SkeletonLoader';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  PlusIcon,
  UserPlusIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { statsService } from '@/services/api';

interface StatsData {
  totalAlunos?: number;
  totalEmpresas?: number;
  totalEstagios?: number;
  totalEstagiosConcluidos?: number;
  totalEstagiosCancelados?: number;
  totalInstituicoes?: number;
  totalCursos?: number;
  totalCidades?: number;
  totalSeguradoras?: number;
  totalRelatorios?: number;
}

const quickActions = [
  {
    name: 'Novo estágio',
    description: 'Cadastrar novo estágio',
    href: '/estagios/novo',
    icon: PlusIcon,
    color: 'from-purple-500 to-purple-600',
  },
  {
    name: 'Novo aluno',
    description: 'Cadastrar novo aluno',
    href: '/alunos/novo',
    icon: UserPlusIcon,
    color: 'from-blue-500 to-blue-600',
  },
  {
    name: 'Nova empresa',
    description: 'Cadastrar nova empresa',
    href: '/empresas/nova',
    icon: BuildingOfficeIcon,
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    name: 'Relatórios',
    description: 'Ver e gerenciar relatórios',
    href: '/relatorios',
    icon: DocumentTextIcon,
    color: 'from-orange-500 to-orange-600',
  },
];

const getStatsConfig = (statsData: StatsData | undefined) => [
  {
    name: 'Alunos',
    value: statsData?.totalAlunos?.toString() || '0',
    icon: UserGroupIcon,
    color: 'text-blue-600',
    bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
    borderColor: 'border-blue-200',
    href: '/alunos',
    description: 'Cadastrados no sistema',
  },
  {
    name: 'Empresas',
    value: statsData?.totalEmpresas?.toString() || '0',
    icon: BuildingOfficeIcon,
    color: 'text-emerald-600',
    bgColor: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
    borderColor: 'border-emerald-200',
    href: '/empresas',
    description: 'Parceiras de estágio',
  },
  {
    name: 'Estágios Ativos',
    value: statsData?.totalEstagios?.toString() || '0',
    icon: ClipboardDocumentListIcon,
    color: 'text-purple-600',
    bgColor: 'bg-gradient-to-br from-purple-50 to-purple-100',
    borderColor: 'border-purple-200',
    href: '/estagios',
    description: 'Em andamento',
  },
  {
    name: 'Instituições',
    value: statsData?.totalInstituicoes?.toString() || '0',
    icon: AcademicCapIcon,
    color: 'text-orange-600',
    bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100',
    borderColor: 'border-orange-200',
    href: '/instituicoes',
    description: 'Educacionais',
  },
];

export const DashboardPage: React.FC = () => {
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: statsService.getDashboardStats,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const stats = getStatsConfig(statsData);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Dashboard</h1>
            <p className="text-blue-100 text-base sm:text-lg">
              Bem-vindo ao {import.meta.env.VITE_APP_TITLE || 'NOME DO SISTEMA'}
            </p>
            <p className="text-blue-200 text-sm mt-1">
              Gerencie estágios, alunos e empresas
            </p>
          </div>
          <div className="hidden sm:block">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center space-x-2">
                <ArrowTrendingUpIcon className="h-6 w-6 text-green-300" />
                <span className="text-sm font-medium">Sistema Ativo</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      {statsLoading ? (
        <SkeletonStats count={4} />
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Link key={stat.name} to={stat.href}>
              <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl ${stat.bgColor} border ${stat.borderColor}`}>
                      <stat.icon className={`h-7 w-7 ${stat.color}`} />
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors">
                        {stat.value}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{stat.name}</h3>
                    <p className="text-sm text-gray-500">{stat.description}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Resumo por status + Ações rápidas */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Resumo por status de estágio */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center text-xl">
              <ClipboardDocumentListIcon className="h-6 w-6 mr-2 text-purple-600" />
              Estágios por status
            </CardTitle>
            <CardDescription className="text-base">
              Visão geral por situação do estágio
            </CardDescription>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="flex gap-4 justify-between">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 flex-1 rounded-xl bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                <Link
                  to="/estagios?status=Ativo"
                  className="flex flex-col items-center rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-4 transition-all hover:shadow-md hover:-translate-y-0.5"
                >
                  <CheckCircleIcon className="h-8 w-8 text-green-600 mb-2" />
                  <span className="text-2xl font-bold text-gray-900">
                    {statsData?.totalEstagios ?? 0}
                  </span>
                  <span className="text-sm font-medium text-green-800">Ativos</span>
                </Link>
                <Link
                  to="/estagios?status=Concluido"
                  className="flex flex-col items-center rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-4 transition-all hover:shadow-md hover:-translate-y-0.5"
                >
                  <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600 mb-2" />
                  <span className="text-2xl font-bold text-gray-900">
                    {statsData?.totalEstagiosConcluidos ?? 0}
                  </span>
                  <span className="text-sm font-medium text-blue-800">Concluídos</span>
                </Link>
                <Link
                  to="/estagios?status=Cancelado"
                  className="flex flex-col items-center rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-red-100 p-4 transition-all hover:shadow-md hover:-translate-y-0.5"
                >
                  <XCircleIcon className="h-8 w-8 text-red-600 mb-2" />
                  <span className="text-2xl font-bold text-gray-900">
                    {statsData?.totalEstagiosCancelados ?? 0}
                  </span>
                  <span className="text-sm font-medium text-red-800">Cancelados</span>
                </Link>
              </div>
            )}
            <div className="mt-4 text-center">
              <Link
                to="/estagios"
                className="text-sm font-medium text-purple-600 hover:text-purple-700"
              >
                Ver todos os estágios →
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Ações rápidas */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center text-xl">
              <PlusIcon className="h-6 w-6 mr-2 text-blue-600" />
              Ações Rápidas
            </CardTitle>
            <CardDescription className="text-base">
              Acesse rapidamente as principais funcionalidades
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {quickActions.map((action) => (
                <Link key={action.name} to={action.href}>
                  <div
                    className={`group bg-gradient-to-br ${action.color} rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer text-white`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 rounded-lg">
                        <action.icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg">{action.name}</h3>
                        <p className="text-white/80 text-sm mt-0.5">{action.description}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center text-xl">
              <ClipboardDocumentListIcon className="h-6 w-6 mr-2 text-emerald-600" />
              Estágios Recentes
            </CardTitle>
            <CardDescription className="text-base">
              Últimos estágios cadastrados no sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            {estagiosLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Carregando estágios...</p>
              </div>
            ) : estagiosRecentes && estagiosRecentes.length > 0 ? (
              <div className="space-y-4">
                {estagiosRecentes.map((estagio) => (
                  <div key={estagio.id} className="group p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl hover:shadow-md transition-all duration-300 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                          {estagio.aluno?.nome || 'Aluno não informado'}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {estagio.empresa?.nome || 'Empresa não informada'}
                        </p>
                      </div>
                      <div className="ml-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${estagio.status === 'Ativo'
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : estagio.status === 'Concluido'
                            ? 'bg-blue-100 text-blue-800 border border-blue-200'
                            : 'bg-red-100 text-red-800 border border-red-200'
                          }`}>
                          {estagio.status === 'Ativo' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
                          {estagio.status === 'Concluido' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
                          {estagio.status !== 'Ativo' && estagio.status !== 'Concluido' && <ExclamationTriangleIcon className="h-3 w-3 mr-1" />}
                          {estagio.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="text-center pt-4 border-t border-gray-200">
                  <Link to="/estagios">
                    <Button variant="outline" className="bg-white hover:bg-gray-50">
                      Ver todos os estágios
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
                  <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum estágio cadastrado</h3>
                  <p className="text-gray-500 mb-4">Comece criando seu primeiro estágio</p>
                  <Link to="/estagios/novo">
                    <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700">
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Criar primeiro estágio
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card> */}
      {/* <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center text-xl">
              <ClockIcon className="h-6 w-6 mr-2 text-purple-600" />
              Atividades Recentes
            </CardTitle>
            <CardDescription className="text-base">
              Últimas atividades realizadas no sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center py-12">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma atividade recente</h3>
                  <p className="text-gray-500 mb-4">As atividades aparecerão aqui conforme você usar o sistema</p>
                  <Button variant="outline" className="mt-2">
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Começar a usar
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card> */}

      {/* Recent Data */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">


        {/* <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center text-xl">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-orange-600" />
              Relatórios Pendentes
            </CardTitle>
            <CardDescription className="text-base">
              Relatórios que precisam ser entregues
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum relatório pendente</h3>
                <p className="text-gray-500 mb-4">Todos os relatórios estão em dia</p>
                <Link to="/relatorios">
                  <Button variant="outline" className="bg-white hover:bg-gray-50">
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    Ver relatórios
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
};
